import React, { useState, SyntheticEvent, useEffect, useRef } from 'react';
import styles from './styles/EmbedApp.module.scss';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { Alert } from '@amzn/awsui-components-core';
import { logger } from 'src/logger';
import { sanitizeEmbeddingUrl } from './utils/EmbeddingUtils';

interface IFrameComponentProps {
  src: string;
  title: string;
  /**
   * When true, force exactly one fresh reload of the iframe after its first load. Workaround for
   * Prophecy next-gen occasionally rendering a blank screen on the first embed when its session
   * cookie is stale: the first load lets Prophecy refresh its own session server-side, and the
   * one-shot reload then renders the app. No-op for apps that don't need it (default false).
   */
  reloadOnce?: boolean;
}

const IFrameComponent: React.FC<IFrameComponentProps> = ({ src, title, reloadOnce = false }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Bumping this remounts the <iframe> element, forcing a fresh browser load of the same src.
  const [reloadNonce, setReloadNonce] = useState<number>(0);
  const hasReloadedRef = useRef<boolean>(false);

  // Reset the one-shot reload guard if the src changes (e.g. environment switch).
  useEffect(() => {
    hasReloadedRef.current = false;
    setReloadNonce(0);
  }, [src]);

  // Add timeout for loading
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setHasError(true);
        setErrorMessage('Loading timed out after 30 seconds');
        logger.warn('Iframe loading timed out');
      }
    }, 30000); // 30 second timeout
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  const handleLoad = (event: SyntheticEvent<HTMLIFrameElement, Event>) => {
    // Prophecy stale-cookie workaround: after the first load, force exactly one fresh reload so
    // the app renders instead of showing a blank screen. Guarded to run at most once per src.
    if (reloadOnce && !hasReloadedRef.current) {
      hasReloadedRef.current = true;
      setIsLoading(true);
      setReloadNonce((n) => n + 1);
      logger.info('Forcing one-time iframe reload (stale-session workaround)');
      return;
    }
    setIsLoading(false);
    setHasError(false);
    logger.info('Iframe loaded successfully!');
  };

  const handleError = (event: SyntheticEvent<HTMLIFrameElement, Event>) => {
    setIsLoading(false);
    setHasError(true);
    logger.error('Error loading iframe:', event);
  };

  // SECURITY: self-defending mount guard. Reuse the SINGLE embed-URL policy
  // (sanitizeEmbeddingUrl) so this component enforces https + the host allowlist
  // + FinSuite self-embed rejection (+ the non-prod localhost allowlist) itself,
  // rather than trusting callers like EmbedApp to have pre-filtered `src`. A
  // future caller passing e.g. https://finsuite.device.finance.amazon.dev/...
  // directly to this component is rejected here. Placed AFTER hooks so the Rules
  // of Hooks hold; rendering the error state (not null) keeps the failure visible.
  if (!sanitizeEmbeddingUrl(src)) {
    logger.warn(`IFrameComponent refused to mount disallowed src: ${src}`);
    return (
      <div className={styles.iframeContainer}>
        <Alert type="error">Invalid URL — cannot be displayed</Alert>
      </div>
    );
  }

  return (
    <div className={styles.iframeContainer}>
      {isLoading && !hasError && <LoadingSpinner />}
      {hasError && <Alert type="error">{errorMessage || 'Error loading content. Please try again.'}</Alert>}
      <iframe
        key={reloadNonce}
        src={src}
        title={title}
        data-embed-app
        onLoad={handleLoad}
        onError={handleError}
        style={{ display: isLoading || hasError ? 'none' : 'block', width: '100%', height: '100%', border: 'none' }}
        // SECURITY: `allow-same-origin` is intentionally present so that genuinely
        // cross-origin embeds (Prophecy, Cloudscape/Polaris, Skynet/Tableau,
        // QuickSight) retain their OWN origin and can access their own
        // cookies/localStorage and authenticate against their own backend.
        //
        // The dangerous combination is `allow-scripts` + `allow-same-origin` on
        // ORIGIN-INHERITING content (`javascript:` / `data:` / `about:blank`) or on
        // content served from FinSuite's own origin — those inherit the embedder's
        // origin and could read `parent.localStorage` (the PoC vector). Keeping the
        // flag is safe ONLY because `src` is guaranteed to be a trusted, cross-origin
        // https URL by the time it reaches here:
        //   1. EmbeddingUtils.sanitizeEmbeddingUrl enforces https-only + a host
        //      allowlist and rejects FinSuite's own origin, and
        //   2. the isHttpsSrc mount guard above is defense-in-depth.
        // A genuinely cross-origin https app receives its own origin, not FinSuite's,
        // so it cannot reach the parent DOM.
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
        referrerPolicy="no-referrer"
      />
    </div>
  );
};

export default IFrameComponent;
